export type AppStatus = 'live' | 'soon'

/** How the Unity shell opens a world. */
export type WorldLaunch = 'native' | 'webview' | 'soon'

/** How the Unity web dashboard loads a live world. */
export type WebEmbedMode = 'iframe' | 'link' | 'none'

export type SuiteAppId =
  | 'equity'
  | 'nightsky'
  | 'roomie'
  | 'grey'
  | 'nothooked'
  | 'brainpower'
  | 'ritmorbit'
  | 'rondoiq'
  | 'scout'
  | 'gamedev'

/** Header / chrome palette — matched to each world's in-app surface. */
export type WorldChromeTheme = {
  mode: 'light' | 'dark'
  bg: string
  line: string
  ink: string
  muted: string
  surface: string
  surfaceBorder: string
  ctaInk: string
  cosmos: string
}

export type SuiteApp = {
  id: SuiteAppId
  name: string
  shortName: string
  domain: string
  href: string | null
  blurb: string
  status: AppStatus
  accent: string
  /** Chrome on the app's own site (landings, marketing). */
  theme: WorldChromeTheme
  /** Hub dashboard shell when the embedded iframe uses a light surface (e.g. journal). */
  embedTheme?: WorldChromeTheme
  /** Shell routing — phase 2+ */
  launch: WorldLaunch
  /** In-app URL when launch is webview */
  webUrl?: string
}

export function getWorldChromeTheme(app: SuiteApp): WorldChromeTheme {
  return app.theme
}

/** Hub workspace header — respects light embed surfaces (Night Sky journal, etc.). */
export function resolveWorkspaceTheme(
  app: SuiteApp,
  embedSurface: 'light' | 'dark' | null = null,
): WorldChromeTheme {
  if (app.embedTheme && (embedSurface === 'light' || (embedSurface === null && app.id === 'nightsky'))) {
    return app.embedTheme
  }
  return app.theme
}

/**
 * Catalog of worlds under Unity Cosmos.
 * Shared by hub web, shell, and Desktop map.
 */
export const SUITE_APPS: ReadonlyArray<SuiteApp> = [
  {
    id: 'equity',
    name: 'Equity Cosmos',
    shortName: 'Equity',
    domain: 'equitycosmos.com',
    href: 'https://equitycosmos.com',
    blurb: 'Personal finance in deep space — accounts, cashflow, and clarity.',
    status: 'live',
    accent: '#6ee7c8',
    theme: {
      mode: 'dark',
      bg: '#061210',
      line: 'rgba(0, 212, 170, 0.22)',
      ink: '#e8f8f4',
      muted: 'rgba(168, 200, 190, 0.88)',
      surface: 'rgba(0, 212, 170, 0.08)',
      surfaceBorder: 'rgba(0, 212, 170, 0.28)',
      ctaInk: '#04140f',
      cosmos: '#3ecfbe',
    },
    launch: 'native',
  },
  {
    id: 'nightsky',
    name: 'Night Sky Diary',
    shortName: 'Night Sky',
    domain: 'nightskydiary.app',
    href: 'https://nightskydiary.app',
    blurb: 'Quiet nights, written under the stars.',
    status: 'live',
    accent: '#9dabff',
    theme: {
      mode: 'dark',
      bg: '#070b16',
      line: 'rgba(155, 184, 255, 0.22)',
      ink: '#eef1f8',
      muted: 'rgba(168, 180, 210, 0.9)',
      surface: 'rgba(155, 184, 255, 0.08)',
      surfaceBorder: 'rgba(155, 184, 255, 0.28)',
      ctaInk: '#0a1020',
      cosmos: '#8eb0ff',
    },
    embedTheme: {
      mode: 'light',
      bg: '#f3f4f6',
      line: 'rgba(209, 213, 219, 0.9)',
      ink: '#1f2937',
      muted: 'rgba(75, 85, 99, 0.92)',
      surface: 'rgba(255, 255, 255, 0.85)',
      surfaceBorder: 'rgba(147, 51, 234, 0.18)',
      ctaInk: '#4c1d95',
      cosmos: '#9333ea',
    },
    launch: 'webview',
    webUrl: 'https://nightskydiary.app',
  },
  {
    id: 'roomie',
    name: 'Roomie Homie',
    shortName: 'Roomie',
    domain: 'roomiehomie.com',
    href: 'https://roomiehomie.com',
    blurb: 'Shared homes without the awkward spreadsheet.',
    status: 'live',
    accent: '#f0a060',
    theme: {
      mode: 'dark',
      bg: '#0a080c',
      line: 'rgba(255, 184, 107, 0.22)',
      ink: '#f6efe8',
      muted: 'rgba(200, 180, 160, 0.9)',
      surface: 'rgba(255, 184, 107, 0.08)',
      surfaceBorder: 'rgba(255, 184, 107, 0.3)',
      ctaInk: '#1a1208',
      cosmos: '#f0a060',
    },
    launch: 'webview',
    webUrl: 'https://roomiehomie.com',
  },
  {
    id: 'grey',
    name: 'Grey Senior',
    shortName: 'Grey Senior',
    domain: 'greysenior.com',
    href: 'https://greysenior.com',
    blurb: 'Dignity, care, and company for later chapters.',
    status: 'live',
    accent: '#bfb09a',
    theme: {
      mode: 'dark',
      bg: '#0c0a08',
      line: 'rgba(196, 181, 160, 0.24)',
      ink: '#ece8e2',
      muted: 'rgba(180, 170, 158, 0.9)',
      surface: 'rgba(196, 181, 160, 0.09)',
      surfaceBorder: 'rgba(196, 181, 160, 0.32)',
      ctaInk: '#1a1510',
      cosmos: '#c4b5a0',
    },
    launch: 'webview',
    webUrl: 'https://greysenior.com',
  },
  {
    id: 'nothooked',
    name: 'Not Hooked',
    shortName: 'Not Hooked',
    domain: 'nothooked.com',
    href: 'https://nothooked.com',
    blurb: 'Cut down, reduce, or quit smoking — daily check-ins without punitive streak resets.',
    status: 'live',
    accent: '#a78bfa',
    theme: {
      mode: 'dark',
      bg: '#120b1c',
      line: 'rgba(241, 234, 249, 0.1)',
      ink: '#f1eaf9',
      muted: 'rgba(179, 163, 199, 0.95)',
      surface: 'rgba(26, 17, 40, 0.82)',
      surfaceBorder: 'rgba(167, 139, 250, 0.28)',
      ctaInk: '#120b1c',
      cosmos: '#d3b8fe',
    },
    launch: 'webview',
    webUrl: 'https://nothooked.com',
  },
  {
    id: 'brainpower',
    name: 'Cosmos Brain Power',
    shortName: 'Brain Power',
    domain: 'cosmosbrainpower.com',
    href: 'https://cosmosbrainpower.com',
    blurb: 'Cognitive training modules — math, language, and critical thinking.',
    status: 'live',
    accent: '#22d3ee',
    theme: {
      mode: 'dark',
      bg: '#0b0f19',
      line: 'rgba(34, 211, 238, 0.22)',
      ink: '#e8f4ff',
      muted: 'rgba(148, 173, 200, 0.9)',
      surface: 'rgba(34, 211, 238, 0.08)',
      surfaceBorder: 'rgba(34, 211, 238, 0.28)',
      ctaInk: '#041018',
      cosmos: '#22d3ee',
    },
    launch: 'webview',
    webUrl: 'https://cosmosbrainpower.com',
  },
  {
    id: 'ritmorbit',
    name: 'ritmorbit',
    shortName: 'ritmorbit',
    domain: 'ritmorbit.com',
    href: 'https://ritmorbit.com',
    blurb: 'Freestyle rap, beat-making, and DJ-style practice.',
    status: 'live',
    accent: '#ff6b9d',
    theme: {
      mode: 'dark',
      bg: '#0c0810',
      line: 'rgba(255, 107, 157, 0.22)',
      ink: '#f8eef4',
      muted: 'rgba(190, 160, 180, 0.9)',
      surface: 'rgba(255, 107, 157, 0.08)',
      surfaceBorder: 'rgba(255, 107, 157, 0.28)',
      ctaInk: '#1a0814',
      cosmos: '#ff6b9d',
    },
    launch: 'webview',
    webUrl: 'https://ritmorbit.com',
  },
  {
    id: 'rondoiq',
    name: 'RondoIQ',
    shortName: 'RondoIQ',
    domain: 'rondoiq.com',
    href: 'https://rondoiq.com',
    blurb: 'Positional football intelligence — game model, rondos, squad and scouting.',
    // Built and working locally (apps/rondoiq, :3004). Flip to 'live' + webview
    // when rondoiq.com is deployed so the hub dashboard embeds it.
    status: 'soon',
    accent: '#16A34A',
    theme: {
      mode: 'dark',
      bg: '#07120D',
      line: 'rgba(22, 163, 74, 0.22)',
      ink: '#F5F1E8',
      muted: 'rgba(200, 220, 205, 0.9)',
      surface: 'rgba(22, 163, 74, 0.08)',
      surfaceBorder: 'rgba(22, 163, 74, 0.28)',
      ctaInk: '#06130c',
      cosmos: '#F59E0B',
    },
    launch: 'webview',
    webUrl: 'https://rondoiq.com',
  },
  {
    id: 'scout',
    name: 'Football Scout',
    shortName: 'Scout',
    domain: 'soon',
    href: null,
    blurb: 'Players, notes, and match scouting.',
    status: 'soon',
    accent: '#f0c050',
    theme: {
      mode: 'dark',
      bg: '#0c0a06',
      line: 'rgba(240, 192, 80, 0.22)',
      ink: '#f8f2e8',
      muted: 'rgba(190, 175, 150, 0.9)',
      surface: 'rgba(240, 192, 80, 0.08)',
      surfaceBorder: 'rgba(240, 192, 80, 0.28)',
      ctaInk: '#1a1408',
      cosmos: '#f0c050',
    },
    launch: 'soon',
  },
  {
    id: 'gamedev',
    name: 'Game Dev',
    shortName: 'Game Dev',
    domain: 'soon',
    href: null,
    blurb: 'Games and tools under the same sky.',
    status: 'soon',
    accent: '#b898f8',
    theme: {
      mode: 'dark',
      bg: '#0a0812',
      line: 'rgba(184, 152, 248, 0.22)',
      ink: '#f0ecf8',
      muted: 'rgba(170, 160, 190, 0.9)',
      surface: 'rgba(184, 152, 248, 0.08)',
      surfaceBorder: 'rgba(184, 152, 248, 0.28)',
      ctaInk: '#120a1a',
      cosmos: '#b898f8',
    },
    launch: 'soon',
  },
]

/** Public orbit — live worlds only (no placeholders on the constellation). */
export const ORBIT_APPS = SUITE_APPS.filter(
  (app) => app.id !== 'scout' && app.id !== 'gamedev' && app.id !== 'rondoiq',
)

export function getSuiteApp(id: SuiteAppId): SuiteApp | undefined {
  return SUITE_APPS.find((a) => a.id === id)
}

/** Worlds the shell can open in-app (native or webview). */
export function isShellWorld(app: SuiteApp): boolean {
  return app.launch === 'native' || app.launch === 'webview'
}

export function resolveWebUrl(app: SuiteApp): string | null {
  if (app.webUrl) return app.webUrl
  if (app.launch === 'webview' && app.href) return app.href
  return null
}

/** Heavy/native worlds open in their own tab; light web worlds embed in iframe. */
export function resolveWebEmbedMode(app: SuiteApp): WebEmbedMode {
  if (app.status !== 'live') return 'none'
  if (app.launch === 'native') return 'link'
  if (app.launch === 'webview') return 'iframe'
  return 'none'
}

export function withUnityFlag(url: string): string {
  try {
    const parsed = new URL(url)
    parsed.searchParams.set('unity', '1')
    return parsed.toString()
  } catch {
    return url
  }
}

/** External launch URL (full app, not iframe). */
export function resolveUnityLaunchUrl(app: SuiteApp): string | null {
  const base = app.href ?? app.webUrl ?? resolveWebUrl(app)
  if (!base) return null
  return withUnityFlag(base)
}

/** In-dashboard iframe URL — only for webview worlds. */
export function resolveUnityEmbedUrl(app: SuiteApp): string | null {
  if (resolveWebEmbedMode(app) !== 'iframe') return null
  const base = resolveWebUrl(app) ?? app.href
  if (!base) return null
  return withUnityFlag(base)
}
